import { PaginatedResponse } from '@frontend/api-utils';
import { AttributeClient, AttributeValueClient } from '@frontend/attribute/api';
import { IGroup } from '@frontend/group/types';
import { ProductAttributeClient, ProductOrderClient } from '@frontend/product/api';
import { IProduct } from '@frontend/product/types';

import { ShopCatalog, ShopFilters, ShopProduct, ShopScope } from './shop.types';

export type ShopProductSort = 'category' | 'name-asc' | 'price-asc' | 'price-desc';

// Read every page of the current category, not the entire catalog. Facets must never
// be computed from just the visible page. Sequential pages keep requests bounded.
export async function fetchShopPages<T>(
    fetchPage: (page: { index: string; size: string }) => Promise<PaginatedResponse<T>>,
    signal?: AbortSignal
): Promise<T[]> {
    const items: T[] = [];
    for (let index = 0; ; index++) {
        if (signal?.aborted) throw new Error('Shop request cancelled');
        const response = await fetchPage({ index: String(index), size: '100' });
        items.push(...response.results);
        if (items.length >= response.count) return items;
        if (response.results.length === 0) throw new Error('Incomplete shop response');
    }
}

const isCategory = (item: IProduct | IGroup): item is IGroup => 'node_type' in item;

export async function fetchShopCatalog(scope: ShopScope, path: string[], signal?: AbortSignal): Promise<ShopCatalog> {
    const fetchCategory = (categoryId: string) =>
        fetchShopPages(
            (page) =>
                ProductOrderClient.fetchCategoryOrderProducts(scope.accountId, categoryId, {
                    ...page,
                    sort_field: 'order',
                    sort_direction: 'asc'
                }),
            signal
        );

    let elements = await fetchCategory(scope.rootCategoryId);
    const breadcrumbs: IGroup[] = [];
    // Validate deep links against the configured tree, including after a refresh.
    for (const categoryId of path) {
        const category = elements.find((item): item is IGroup => isCategory(item) && item.id === categoryId);
        if (!category) throw new Error('Category is outside this shop');
        breadcrumbs.push(category);
        elements = await fetchCategory(categoryId);
    }
    const categories = elements.filter(isCategory);
    const products = elements.filter((item): item is IProduct => !isCategory(item));
    if (!products.length) return { breadcrumbs, categories, products: [], attributes: [] };

    const productAttributes = (
        await ProductAttributeClient.resolveProductAttributes(
            products.map((product) => product.id),
            'product_id'
        )
    ).results;
    const attributeIds = [...new Set(productAttributes.map((item) => item.attribute_id))];
    const valueIds = [...new Set(productAttributes.map((item) => item.value_id))];
    const [attributes, values] = await Promise.all([
        attributeIds.length ? AttributeClient.resolveAttributes(attributeIds).then((response) => response.results) : [],
        valueIds.length ? AttributeValueClient.resolveAttributeValues(valueIds).then((response) => response.results) : []
    ]);
    const byProduct = new Map<string, typeof productAttributes>();
    const valueLabels = new Map(values.map((value) => [value.id, value.value]));
    for (const attribute of productAttributes) {
        const list = byProduct.get(attribute.product_id) ?? [];
        list.push(attribute);
        byProduct.set(attribute.product_id, list);
    }
    return {
        breadcrumbs,
        categories,
        products: products.map((product) => ({
            ...product,
            imageCategory: breadcrumbs.length
                ? { account_id: breadcrumbs[breadcrumbs.length - 1].account_id, id: breadcrumbs[breadcrumbs.length - 1].id }
                : { account_id: scope.accountId, id: scope.rootCategoryId },
            productAttributes: byProduct.get(product.id) ?? [],
            attributeValues: (byProduct.get(product.id) ?? [])
                .map((attribute) => valueLabels.get(attribute.value_id))
                .filter((value): value is string => value !== undefined)
        })),
        attributes: attributes.map((attribute) => ({
            attribute,
            values: values.filter((value) => value.attribute_id === attribute.id).sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
        }))
    };
}

export function matchesShopFilters(product: ShopProduct, filters: ShopFilters, ignoreAttribute?: string): boolean {
    return Object.entries(filters).every(
        ([attributeId, values]) =>
            attributeId === ignoreAttribute ||
            values.length === 0 ||
            product.productAttributes.some((attribute) => attribute.attribute_id === attributeId && values.includes(attribute.value_id))
    );
}

export function countShopFacetValues(products: ShopProduct[], filters: ShopFilters, attributeId: string): Record<string, number> {
    const counts: Record<string, number> = {};
    for (const product of products) {
        if (!matchesShopFilters(product, filters, attributeId)) continue;
        const seen = new Set<string>();
        for (const attribute of product.productAttributes) {
            if (attribute.attribute_id !== attributeId || seen.has(attribute.value_id)) continue;
            seen.add(attribute.value_id);
            counts[attribute.value_id] = (counts[attribute.value_id] ?? 0) + 1;
        }
    }
    return counts;
}

export function readShopFilters(params: URLSearchParams): ShopFilters {
    const filters: ShopFilters = {};
    for (const key of new Set(params.keys())) {
        if (key.startsWith('attribute.')) filters[key.slice('attribute.'.length)] = params.getAll(key);
    }
    return filters;
}

export function readShopProductSort(value: string | null): ShopProductSort {
    return value === 'name-asc' || value === 'price-asc' || value === 'price-desc' ? value : 'category';
}

export function findShopProducts(products: ShopProduct[], filters: ShopFilters, query: string, sort: ShopProductSort): ShopProduct[] {
    const normalizedQuery = query.trim().toLocaleLowerCase();
    const result = products.filter((product) => {
        if (!matchesShopFilters(product, filters)) return false;
        if (!normalizedQuery) return true;
        return [product.name, product.name_en_us, product.name_nl_be, product.name_fr_fr, product.name_de_de, product.sku, product.ean]
            .filter((value): value is string => !!value)
            .some((value) => value.toLocaleLowerCase().includes(normalizedQuery));
    });
    if (sort === 'category') return result;
    return [...result].sort((left, right) => {
        if (sort === 'name-asc') return left.name.localeCompare(right.name);
        if (left.price == null) return right.price == null ? 0 : 1;
        if (right.price == null) return -1;
        const leftPrice = left.price;
        const rightPrice = right.price;
        return sort === 'price-asc' ? leftPrice - rightPrice : rightPrice - leftPrice;
    });
}
